import React, { useState, useEffect, useRef } from 'react';

function CalendarIcon({ d, size = 14 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

const pillBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7,
  background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
  color: 'var(--fg-default)', padding: '7px 12px', borderRadius: 8,
  fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const toISO = (d: Date) => d.toISOString().slice(0, 10);

// Shared with pages that want to default their date range to the same
// "Last 7 days" preset shown in this picker's dropdown.
export function last7DaysRange(): { start: string; end: string } {
  const now = new Date();
  return { start: toISO(new Date(now.getTime() - 6 * 86400000)), end: toISO(now) };
}

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  // Which edge of the trigger button the dropdown panel hangs from. 'left'
  // (default) suits a picker anchored near the left of its container; use
  // 'right' when the trigger sits near the right edge of the page so the
  // panel doesn't run off the viewport.
  align?: 'left' | 'right';
}

// Reusable date-range picker: presets + explicit start/end date inputs.
// Shared between the campaign dashboard and the creative detail page so
// both pages behave identically.
export function DateRangePicker({ startDate, endDate, onDateChange, align = 'left' }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(startDate ?? '');
  const [localEnd, setLocalEnd] = useState(endDate ?? '');
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setLocalStart(startDate ?? ''); setLocalEnd(endDate ?? ''); }, [startDate, endDate, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fmtDate = (s: string) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const dateLabel = startDate ? `${fmtDate(startDate)}${endDate ? ` – ${fmtDate(endDate)}` : ''}` : 'All time';
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--bg-surface)', border: '1px solid var(--border-soft)', borderRadius: 7, fontSize: 12, color: 'var(--fg-strong)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const PRESETS = [
    { label: 'Yesterday',    start: toISO(yesterday), end: toISO(yesterday) },
    { label: 'Last 7 days',  ...last7DaysRange() },
    { label: 'Last 30 days', start: toISO(new Date(now.getTime() - 29 * 86400000)), end: toISO(now) },
    { label: 'This month',   start: toISO(new Date(now.getFullYear(), now.getMonth(), 1)), end: toISO(now) },
    { label: 'Last month',   start: toISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: toISO(new Date(now.getFullYear(), now.getMonth(), 0)) },
  ];

  return (
    <div style={{ position: 'relative' }} ref={pickerRef}>
      <button style={{ ...pillBtnStyle, background: open ? 'var(--bg-surface-2)' : undefined }} onClick={() => setOpen(o => !o)}>
        <CalendarIcon d="M3 4h18v18H3zM3 10h18M8 2v4M16 2v4" size={14} />
        <span style={{ fontWeight: 700, color: 'var(--fg-strong)' }} className="num">{dateLabel}</span>
        <CalendarIcon d="M6 9l6 6 6-6" size={12} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', [align]: 0, zIndex: 100, background: 'var(--bg-surface)', border: '1px solid var(--border-soft)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', overflow: 'hidden' }}>
          <div style={{ borderRight: '1px solid var(--border-subtle)', padding: '8px 0', display: 'flex', flexDirection: 'column', minWidth: 130 }}>
            {PRESETS.map(p => {
              const active = localStart === p.start && localEnd === p.end;
              return (
                <button key={p.label} onClick={() => { onDateChange(p.start, p.end); setOpen(false); }}
                  style={{ padding: '8px 16px', background: active ? 'var(--accent-soft-bg)' : 'transparent', color: active ? 'var(--brand-green-500)' : 'var(--fg-default)', fontWeight: active ? 700 : 500, border: 'none', textAlign: 'left', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 250 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-muted)', display: 'block', marginBottom: 5 }}>Start</label>
                <input type="date" value={localStart} onChange={e => setLocalStart(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-muted)', display: 'block', marginBottom: 5 }}>End</label>
                <input type="date" value={localEnd} onChange={e => setLocalEnd(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setOpen(false)} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid var(--border-soft)', borderRadius: 7, fontSize: 12, color: 'var(--fg-default)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={() => { onDateChange(localStart, localEnd); setOpen(false); }} disabled={!localStart} style={{ padding: '7px 14px', background: localStart ? 'var(--brand-navy-800)' : 'var(--bg-surface-3)', color: localStart ? '#fff' : 'var(--fg-faint)', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: localStart ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
